import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Badge,
  Button,
  InputGroup,
  Form,
  Nav,
  Modal,
  Card,
  Spinner,
} from "react-bootstrap";
import {
  BsSearch,
  BsEye,
  BsStars,
  BsTelephoneOutbound,
  BsTelephoneFill,
  BsBuildings,
  BsCheckCircle,
  BsClockHistory,
  BsXCircle,
  BsGrid,
} from "react-icons/bs";
import { toast } from "react-toastify";
import WebService from "../../../Services/WebService";
import HelperService from "../../../Services/HelperService";
import RmAiEmailModal from "../Modals/RmAiEmailModal";
import RmLogActivityModal from "../Modals/RmLogActivityModal";
import RmVoiceCallModal from "../Modals/RmVoiceCallModal";
import PrimePagination from "../../Common/SSPagination/PrimePagination";

const RmEntrepreneurs = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"INVESTMENT" | "WANT_TO_GROW" | "COMBO">("INVESTMENT");
  const [statusTab, setStatusTab] = useState<"request" | "verified">("request");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loader, setLoader] = useState(false);
  const [detailsLoader, setDetailsLoader] = useState(false);
  const [list, setList] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [keyword, setKeyword] = useState("");

  // Details modal state
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [profileDetails, setProfileDetails] = useState<any>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Action modals
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callUser, setCallUser] = useState<any>(null);

  const openCallModal = (user: any) => {
    setCallUser(user);
    setShowCallModal(true);
  };

  const itemsPerPage = 10;

  useEffect(() => {
    fetchEntrepreneurs(keyword, activeTab, statusTab, currentPage);
  }, [activeTab, statusTab, currentPage]);

  const fetchEntrepreneurs = (searchKw: string, tab: string, status: string, page: number) => {
    setLoader(true);
    let resolvedStatus = status;
    if (status === "request") {
      resolvedStatus = "PENDING";
    } else if (status === "verified" || status === "approved") {
      resolvedStatus = "VERIFIED";
    }

    const apiOffset = page - 1;
    const subType =
      tab === "INVESTMENT"
        ? "investment"
        : tab === "WANT_TO_GROW"
          ? "want-to-grow"
          : tab === "COMBO"
            ? "combo"
            : tab.toLowerCase();

    const endpoint = `rm-user/list/entrepreneur/${subType}?status=${resolvedStatus}&offset=${apiOffset}&keyword=${searchKw}&country=&from_date=&to_date=&order_by=&sort_by=`;

    WebService.getAPI({ action: endpoint, body: null })
      .then((res: any) => {
        setList(res?.list || []);
        setTotalCount(res?.count || res?.list?.length || 0);
        setLoader(false);
      })
      .catch((error: any) => {
        setLoader(false);
        toast.error(error?.response?.data?.message || "Failed to load entrepreneurs");
      });
  };

  const handleTabChange = (tab: "INVESTMENT" | "WANT_TO_GROW" | "COMBO") => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleStatusChange = (status: "request" | "verified") => {
    setStatusTab(status);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchEntrepreneurs(keyword, activeTab, statusTab, 1);
  };

  const openViewDetails = (item: any) => {
    const profileId = item?.uuid || item?.id;
    const resolvedType = (item?.profile_type || activeTab).toLowerCase();
    navigate(`/rm/entrepreneurs/details/${profileId}/${resolvedType}`);
  };

  const openEmailModal = (user: any) => {
    setSelectedUser(user);
    setShowEmailModal(true);
  };

  const openLogModal = (user: any) => {
    setSelectedUser(user);
    setShowLogModal(true);
  };

  const getSrNo = (index: number) => {
    return (currentPage - 1) * itemsPerPage + index + 1;
  };

  const getCreatedByName = (item: any) => {
    if (!item) return null;
    const createdBy =
      (typeof item?.created_by === "object" ? item?.created_by : null) ||
      item?.creator ||
      item?.users?.creator ||
      (typeof item?.created_by === "string" ? item?.created_by : null) ||
      (typeof item?.users?.created_by === "object"
        ? item?.users?.created_by
        : null);

    if (typeof createdBy === "string" && isNaN(Number(createdBy)))
      return createdBy;
    if (typeof createdBy === "object" && createdBy?.name) return createdBy.name;
    if (item?.created_by_name) return item.created_by_name;
    return null;
  };

  const getAiOverview = (item: any) => {
    if (!item) return null;
    if (activeTab === "INVESTMENT" && item?.ai_scores?.investment?.overall)
      return item.ai_scores.investment.overall;
    if (activeTab === "WANT_TO_GROW" && item?.ai_scores?.["want-to-grow"]?.overall)
      return item.ai_scores["want-to-grow"].overall;
    if (activeTab === "COMBO" && item?.ai_scores?.combo?.overall)
      return item.ai_scores.combo.overall;

    if (item?.ai_scores?.investment?.overall)
      return item.ai_scores.investment.overall;
    if (item?.ai_scores?.["want-to-grow"]?.overall)
      return item.ai_scores["want-to-grow"].overall;
    if (item?.ai_scores?.overall) return item.ai_scores.overall;
    return null;
  };

  const getScoreClassForAi = (score?: number | string | null) => {
    const value = Number(score);
    if (Number.isNaN(value)) return "weak";
    if (value >= 90) return "expert";
    if (value >= 70) return "strong";
    if (value >= 40) return "moderate";
    if (value > 0) return "incomplete";
    return "weak";
  };

  const getStrengthClassForAi = (
    strength?: string | null,
    score?: number | string | null
  ) => {
    const normalized = HelperService.removeUnderScore(strength || "")
      ?.toLowerCase()
      ?.trim();

    if (normalized?.includes("expert")) return "expert";
    if (normalized?.includes("strong")) return "strong";
    if (normalized?.includes("moderate")) return "moderate";
    if (normalized?.includes("incomplete")) return "incomplete";
    if (normalized?.includes("weak")) return "weak";

    return getScoreClassForAi(score);
  };

  const getViewCount = (item: any) => {
    if (activeTab === "INVESTMENT") return item?.investment_profile_view_count || 0;
    if (activeTab === "WANT_TO_GROW") return item?.want_to_grow_profile_view_count || 0;
    if (activeTab === "COMBO") return item?.combo_profile_view_count || 0;
    return item?.investment_profile_view_count || 0;
  };

  return (
    <div className="page-content pb-5" style={{ minHeight: "calc(100vh - 70px)", background: "#f8f9fa" }}>
      <Container fluid className="px-4 pt-3">
        {/* Header Title */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div>
            <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <BsBuildings style={{ color: "#3fb564" }} /> Entrepreneurs Directory (View Mode)
            </h4>
            <p className="text-muted font-13 mb-0">
              View live entrepreneur profiles, filter by Request & Approved status, draft AI emails, and log interactions.
            </p>
          </div>
          <Badge pill className="px-3 py-2 font-13 text-white" style={{ backgroundColor: "#3fb564" }}>
            Total Records: {totalCount}
          </Badge>
        </div>

        {/* Main Category Tabs & Search Controls */}
        <Row className="g-3 align-items-center mb-3">
          <Col lg={7}>
            <Nav variant="pills" className="bg-white p-1.5 rounded-pill border shadow-xs d-inline-flex">
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "INVESTMENT"}
                  onClick={() => handleTabChange("INVESTMENT")}
                  className={`rounded-pill font-13 fw-semibold px-4 py-1.5 ${
                    activeTab === "INVESTMENT" ? "text-white" : "text-dark"
                  }`}
                  style={activeTab === "INVESTMENT" ? { backgroundColor: "#3fb564" } : {}}
                >
                  Capital Bridge
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "WANT_TO_GROW"}
                  onClick={() => handleTabChange("WANT_TO_GROW")}
                  className={`rounded-pill font-13 fw-semibold px-4 py-1.5 ${
                    activeTab === "WANT_TO_GROW" ? "text-white" : "text-dark"
                  }`}
                  style={activeTab === "WANT_TO_GROW" ? { backgroundColor: "#3fb564" } : {}}
                >
                  Global
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "COMBO"}
                  onClick={() => handleTabChange("COMBO")}
                  className={`rounded-pill font-13 fw-semibold px-4 py-1.5 ${
                    activeTab === "COMBO" ? "text-white" : "text-dark"
                  }`}
                  style={activeTab === "COMBO" ? { backgroundColor: "#3fb564" } : {}}
                >
                  Combo
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>

          <Col lg={5}>
            <InputGroup className="shadow-xs rounded-pill overflow-hidden border">
              <Form.Control
                placeholder="Search entrepreneur name, company, email..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
              <Button style={{ backgroundColor: "#3fb564", borderColor: "#3fb564" }} onClick={handleSearch}>
                <BsSearch /> Search
              </Button>
            </InputGroup>
          </Col>
        </Row>

        {/* Status Sub-Tabs (Request, Approved) */}
        <div className="d-flex align-items-center gap-2 mb-4 bg-white p-2 rounded-4 border shadow-xs overflow-auto">
          <span className="text-muted font-12 fw-bold text-uppercase px-2">Status:</span>
          <Button
            size="sm"
            className={`rounded-pill px-3.5 font-12 fw-semibold d-flex align-items-center gap-1 ${
              statusTab === "request" ? "text-white" : "btn-light text-secondary"
            }`}
            style={statusTab === "request" ? { backgroundColor: "#3fb564", borderColor: "#3fb564" } : {}}
            onClick={() => handleStatusChange("request")}
          >
            <BsClockHistory size={13} /> Request
          </Button>
          <Button
            size="sm"
            className={`rounded-pill px-3.5 font-12 fw-semibold d-flex align-items-center gap-1 ${
              statusTab === "verified" ? "text-white" : "btn-light text-secondary"
            }`}
            style={statusTab === "verified" ? { backgroundColor: "#3fb564", borderColor: "#3fb564" } : {}}
            onClick={() => handleStatusChange("verified")}
          >
            <BsCheckCircle size={13} /> Approved
          </Button>
        </div>

        {/* Data Table */}
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-3">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0 table-style-1">
              <thead className="bg-light">
                <tr className="text-center">
                  <th className="py-3 px-2" style={{ minWidth: "60px" }}>#</th>
                  <th className="py-3" style={{ minWidth: "120px" }}>Date</th>
                  <th className="py-3" style={{ minWidth: "160px" }}>Name</th>
                  <th className="py-3" style={{ minWidth: "180px" }}>Email ID</th>
                  <th className="py-3" style={{ minWidth: "130px" }}>Mobile No.</th>
                  <th className="py-3" style={{ minWidth: "120px" }}>Country</th>
                  <th className="py-3" style={{ minWidth: "90px" }}>AI Score</th>
                  <th className="py-3" style={{ minWidth: "100px" }}>Strength</th>
                  <th className="py-3" style={{ minWidth: "100px" }}>Status</th>
                  <th className="py-3 text-center" style={{ minWidth: "160px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loader ? (
                  <tr>
                    <td colSpan={10} className="text-center py-5">
                      <Spinner animation="border" style={{ color: "#3fb564" }} />
                      <p className="text-muted font-13 mt-2">Fetching entrepreneurs list from server...</p>
                    </td>
                  </tr>
                ) : list && list.length > 0 ? (
                  list.map((item: any, idx: number) => {
                    const userNameStr = item?.users?.name || item?.name || "-";
                    const emailStr = item?.users?.email || item?.email || "-";
                    const mobileStr = item?.display_number || (item?.users?.mobile_number ? `${item?.users?.dial_code || ""}-${item?.users?.mobile_number}` : "-");
                    const countryStr = item?.country || item?.users?.country || "-";
                    const statusStr = item?.users?.status || item?.status || "ACTIVE";
                    const createdByName = getCreatedByName(item);
                    const ai = getAiOverview(item);
                    const aiClass = getStrengthClassForAi(ai?.strength || "", ai?.score);

                    return (
                      <tr key={idx} className="text-center">
                        <td className="px-2 font-13 fw-semibold">{getSrNo(idx)}</td>
                        <td>
                          <div className="font-13">
                            {item?.created_at ? HelperService.formatDate(item?.created_at) : "-"}
                          </div>
                          {createdByName && (
                            <div className="text-muted font-11 text-nowrap mt-1">
                              By: {createdByName}
                            </div>
                          )}
                        </td>
                        <td>
                          <div
                            className="fw-bold text-dark font-14 text-primary-hover"
                            style={{ cursor: "pointer" }}
                            onClick={() => openViewDetails(item)}
                          >
                            {userNameStr}
                          </div>
                          {item?.status === "GUEST_PENDING" && (
                            <Badge className="badge badge-warning mt-1 font-11">
                              Guest Pending
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className="text-muted font-13">{emailStr}</div>
                        </td>
                        <td className="font-13 text-muted">{mobileStr}</td>
                        <td className="font-13">{countryStr}</td>
                        <td className="profile-strength">
                          <span className={ai?.score > 0 ? aiClass : ""}>
                            {ai?.score ?? "-"}
                          </span>
                        </td>
                        <td className="profile-strength">
                          <span className={ai?.strength ? aiClass : ""}>
                            {HelperService.removeUnderScore(ai?.strength) ?? "-"}
                          </span>
                        </td>
                        <td>
                          <Badge className={HelperService.decideBadgeClass(statusStr)}>
                            {statusStr}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-1.5">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="rounded-circle p-1.5"
                              title="View Full Profile Details"
                              onClick={() => openViewDetails(item)}
                            >
                              <BsEye size={15} />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="rounded-circle p-1.5"
                              title="Send Email / AI Draft"
                              onClick={() => openEmailModal(item)}
                            >
                              <BsStars size={15} />
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              className="rounded-circle p-1.5"
                              title="Call via 2Chat Voice"
                              onClick={() => openCallModal(item)}
                            >
                              <BsTelephoneFill size={13} />
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              className="rounded-circle p-1.5 text-dark"
                              title="Log Call"
                              onClick={() => openLogModal(item)}
                            >
                              <BsTelephoneOutbound size={15} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-muted font-14">
                      No entrepreneur profiles found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>

        {/* Pagination */}
        {!loader && totalCount > itemsPerPage && (
          <div className="d-flex justify-content-end me-2">
            <PrimePagination
              totalCount={totalCount}
              itemCountPerPage={itemsPerPage}
              currentPage={currentPage}
              changePage={(page: number) => {
                setCurrentPage(page);
              }}
            />
          </div>
        )}
      </Container>

      {/* View Mode Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="font-18 fw-bold text-dark d-flex align-items-center gap-2">
            <BsBuildings className="text-primary" /> Entrepreneur Details (View Only)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          {detailsLoader ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted font-13 mt-2">Loading profile details from server...</p>
            </div>
          ) : (
            <div>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Entrepreneur Name</small>
                    <h5 className="fw-bold text-dark mb-0">
                      {profileDetails?.users?.name || selectedUser?.users?.name || selectedUser?.name || "-"}
                    </h5>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Email Address</small>
                    <h5 className="fw-bold text-primary mb-0">
                      {profileDetails?.users?.email || selectedUser?.users?.email || selectedUser?.email || "-"}
                    </h5>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Mobile Number</small>
                    <p className="fw-semibold text-dark mb-0">
                      {profileDetails?.users?.mobile_number
                        ? `${profileDetails?.users?.dial_code || ""}-${profileDetails?.users?.mobile_number}`
                        : selectedUser?.users?.mobile_number || "-"}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Company Name</small>
                    <p className="fw-semibold text-dark mb-0">
                      {profileDetails?.entrepreneur_business_details?.company_name ||
                        selectedUser?.company_name ||
                        selectedUser?.users?.companies?.company_name ||
                        "-"}
                    </p>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Industry / Sector</small>
                    <p className="fw-semibold text-dark mb-0">
                      {profileDetails?.entrepreneur_business_details?.industry_name ||
                        profileDetails?.entrepreneur_business_details?.industry ||
                        "-"}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Investment / Funding Goal</small>
                    <p className="fw-semibold text-primary mb-0">
                      {profileDetails?.entrepreneur_business_details?.investment_amount ||
                        profileDetails?.entrepreneur_business_details?.funding_amount ||
                        "-"}
                    </p>
                  </div>
                </Col>
              </Row>

              <div className="bg-light p-3 rounded-3 mb-3">
                <small className="text-muted font-12 fw-semibold text-uppercase">Business Overview & Description</small>
                <p className="font-14 text-dark mb-0 mt-1">
                  {profileDetails?.entrepreneur_business_details?.business_description ||
                    profileDetails?.entrepreneur_business_details?.description ||
                    selectedUser?.business_description ||
                    "No additional description provided."}
                </p>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="outline-success"
                  onClick={() => {
                    setShowViewModal(false);
                    openEmailModal(selectedUser);
                  }}
                >
                  <BsStars className="me-1" /> Compose AI Mail
                </Button>
                <Button
                  variant="outline-info"
                  onClick={() => {
                    setShowViewModal(false);
                    openCallModal(selectedUser);
                  }}
                >
                  <BsTelephoneFill className="me-1" /> Voice Call
                </Button>
                <Button
                  variant="outline-warning"
                  className="text-dark"
                  onClick={() => {
                    setShowViewModal(false);
                    openLogModal(selectedUser);
                  }}
                >
                  <BsTelephoneOutbound className="me-1" /> Log Interaction
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Action Modals */}
      <RmAiEmailModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        recipientEmail={selectedUser?.users?.email || selectedUser?.email || ""}
        recipientName={selectedUser?.users?.name || selectedUser?.name || ""}
        userType="ENTREPRENEUR"
        userId={selectedUser?.users?.id || selectedUser?.id || selectedUser?.uuid}
      />

      <RmVoiceCallModal
        show={showCallModal}
        onHide={() => setShowCallModal(false)}
        user={callUser}
        userType="ENTREPRENEUR"
        onLogCall={(u, dur) => {
          setSelectedUser(u);
          setShowLogModal(true);
        }}
      />

      <RmLogActivityModal
        show={showLogModal}
        onHide={() => setShowLogModal(false)}
        contactName={selectedUser?.users?.name || selectedUser?.name || ""}
        contactEmail={selectedUser?.users?.email || selectedUser?.email || ""}
        userType="ENTREPRENEUR"
      />
    </div>
  );
};

export default RmEntrepreneurs;
